import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Transaction, TransactionCategory } from "@shared/schema";
import { useTransactionCategories } from "@/hooks/use-transactions";
import { formatCurrency } from "@/lib/utils";
import { Trash2, PlusCircle, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface SplitEntry {
  id?: number;
  description: string;
  amount: string;
  categoryId: number | null;
}

interface SplitTransactionDialogProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function SplitTransactionDialog({
  transaction,
  isOpen,
  onOpenChange,
  onSuccess
}: SplitTransactionDialogProps) {
  const queryClient = useQueryClient();
  const { categories } = useTransactionCategories();
  const { toast } = useToast();

  // Lightweight split mutation — avoids the heavy useTransactions() hook
  const { mutateAsync: splitTransaction, isPending: isPendingSplit } = useMutation({
    mutationFn: async ({ transactionId, splitEntries }: {
      transactionId: number;
      splitEntries: Array<{ description: string; amount: string; categoryId?: number | null }>;
    }) => {
      const businessId = transaction?.businessId;
      if (!businessId) throw new Error("No business ID");
      const response = await fetch(
        `/api/businesses/${businessId}/transactions/${transactionId}/split`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ splitEntries }),
          credentials: "include",
        }
      );
      return response;
    },
    onSuccess: (response) => {
      if (response.ok && transaction?.businessId) {
        queryClient.invalidateQueries({
          predicate: (q) => !!q.queryKey[0]?.toString().includes(`/api/businesses/${transaction.businessId}/transactions`),
        });
      }
    },
  });

  const [splitEntries, setSplitEntries] = useState<SplitEntry[]>([]);
  const [originalAmount, setOriginalAmount] = useState<number>(0);

  // Initialize splits ONLY when modal opens or transaction id changes
  // Do NOT include splitEntries or transaction object in deps
  const txnId = transaction?.id;
  const txnIsSplit = transaction?.isSplit;
  const txnBusinessId = transaction?.businessId;
  const txnDescription = transaction?.description;
  const txnAmount = transaction?.amount;
  const txnCategoryId = (transaction as any)?.coaAccountId
    ?? (transaction as any)?.coa_account_id
    ?? transaction?.categoryId
    ?? null;

  useEffect(() => {
    if (!isOpen || !txnId) return;

    const amountStr = String(txnAmount || '0');
    const parsedAmount = parseFloat(amountStr.replace(/[^\d.-]/g, ''));
    setOriginalAmount(parsedAmount);

    if (txnIsSplit) {
      // Fetch existing split entries from API
      fetch(`/api/businesses/${txnBusinessId}/transactions/${txnId}`, { credentials: "include" })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (data.splitEntries && data.splitEntries.length > 0) {
            setSplitEntries(data.splitEntries.map((entry: any) => ({
              id: entry.id,
              description: entry.description,
              amount: String(entry.amount),
              categoryId: entry.categoryId ?? null,
            })));
          } else {
            setSplitEntries([{
              description: txnDescription || "",
              amount: parsedAmount.toFixed(2),
              categoryId: txnCategoryId,
            }]);
          }
        })
        .catch(() => {
          setSplitEntries([{
            description: txnDescription || "",
            amount: parsedAmount.toFixed(2),
            categoryId: txnCategoryId,
          }]);
        });
    } else {
      // Not split yet — single entry with full amount
      setSplitEntries([{
        description: txnDescription || "",
        amount: parsedAmount.toFixed(2),
        categoryId: txnCategoryId,
      }]);
    }
  }, [isOpen, txnId]);

  // Derive totals from splitEntries — no useEffect needed
  const totalAmount = useMemo(() => {
    const total = splitEntries.reduce((sum, entry) => {
      const amt = parseFloat(entry.amount?.replace(/[^\d.-]/g, '') || "0");
      return sum + (isNaN(amt) ? 0 : amt);
    }, 0);
    return Math.round(total * 100) / 100;
  }, [splitEntries]);

  const roundedOriginal = Math.round(originalAmount * 100) / 100;
  const amountDifference = roundedOriginal - totalAmount;
  const validationError =
    Math.abs(amountDifference) > 0.01
      ? "Split amounts must equal the original transaction amount"
      : null;

  const handleAddSplitEntry = () => {
    const newAmount = amountDifference > 0 ? amountDifference.toFixed(2) : "0";
    setSplitEntries(prev => [
      ...prev,
      {
        description: `Split ${prev.length + 1}`,
        amount: newAmount,
        categoryId: null,
      }
    ]);
  };

  const handleRemoveSplitEntry = (index: number) => {
    if (splitEntries.length <= 1) {
      toast({
        title: "Cannot remove entry",
        description: "At least one split entry is required",
        variant: "destructive"
      });
      return;
    }
    setSplitEntries(prev => prev.filter((_, i) => i !== index));
  };

  const handleEntryChange = (index: number, field: keyof SplitEntry, value: string | number | null) => {
    setSplitEntries(prev => {
      const updated = [...prev];
      if (field === 'amount') {
        const numericValue = typeof value === 'string'
          ? value.replace(/[^0-9.]/g, '')
          : String(value || '');
        updated[index] = { ...updated[index], [field]: numericValue };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!transaction) return;

    if (validationError) {
      toast({
        title: "Validation Error",
        description: validationError,
        variant: "destructive"
      });
      return;
    }

    try {
      const preparedEntries = splitEntries.map(entry => ({
        description: entry.description || `Split item`,
        amount: parseFloat(entry.amount || "0").toFixed(2),
        categoryId: entry.categoryId === 0 ? null : entry.categoryId
      }));

      const response = await splitTransaction({
        transactionId: transaction.id,
        splitEntries: preparedEntries
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to split transaction");
      }

      toast({
        title: "Transaction Split",
        description: "Transaction has been split successfully",
      });

      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Error splitting transaction:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to split transaction",
        variant: "destructive"
      });
    }
  };

  // Filter categories based on transaction type
  const filteredCategories = categories.filter(cat =>
    !transaction?.type || cat.name.toLowerCase() !== (transaction.type === 'income' ? 'expense' : 'income')
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Split Transaction</DialogTitle>
          <DialogDescription>
            Divide this {formatCurrency(originalAmount)} transaction into multiple categories.
            The sum of all splits must equal the original amount.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-between items-center mb-2">
            <div className="text-sm font-medium">Original Amount: {formatCurrency(originalAmount)}</div>
            <div className="text-sm font-medium">
              Remaining: {formatCurrency(amountDifference)}
              <span className={amountDifference !== 0 ? "text-red-500 ml-1" : "text-green-500 ml-1"}>
                {amountDifference === 0 ? "✓" : ""}
              </span>
            </div>
          </div>

          <div className="divide-y">
            {splitEntries.map((entry, index) => (
              <div key={index} className="py-4 first:pt-0 grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <Label htmlFor={`description-${index}`}>Description</Label>
                  <Input
                    id={`description-${index}`}
                    value={entry.description}
                    onChange={(e) => handleEntryChange(index, "description", e.target.value)}
                    placeholder="Description"
                  />
                </div>

                <div className="col-span-3">
                  <Label htmlFor={`amount-${index}`}>Amount</Label>
                  <Input
                    id={`amount-${index}`}
                    value={entry.amount}
                    onChange={(e) => handleEntryChange(index, "amount", e.target.value)}
                    placeholder="0.00"
                    type="text"
                    inputMode="decimal"
                  />
                </div>

                <div className="col-span-4">
                  <Label htmlFor={`category-${index}`}>Category</Label>
                  <Select
                    value={entry.categoryId?.toString() || "0"}
                    onValueChange={(value) => handleEntryChange(index, "categoryId", value === "0" ? null : parseInt(value))}
                  >
                    <SelectTrigger id={`category-${index}`}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No Category</SelectItem>
                      {filteredCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-1 flex items-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveSplitEntry(index)}
                    disabled={splitEntries.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            onClick={handleAddSplitEntry}
            className="w-full mt-2"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Split
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPendingSplit || !!validationError || Math.abs(amountDifference) > 0.01}
          >
            {isPendingSplit ? "Saving..." : "Save Splits"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
